/**
 * Fait : LA politique d'arbitrage par version des écritures sur un projet —
 *   envoyer avec la version qu'on croit à jour, et si le serveur répond
 *   « conflit », relire la version puis rejouer UNE fois. Elle est écrite ici,
 *   une seule fois, pour les deux gestes qui la demandent : renommer et
 *   archiver.
 *
 *   POURQUOI ELLE EXISTE, écrit le 2026-08-23 : elle était recopiée dans la
 *   fiche du projet pour le renommage, et le geste d'archivage allait la
 *   recopier une seconde fois. Deux copies d'une politique, c'est une copie
 *   qu'on corrigera un jour sans l'autre.
 *
 *   POURQUOI REJOUER, plutôt que laisser recliquer (critère 21) : l'écran
 *   n'affiche pas la version, personne ne sait donc ce qu'il faudrait
 *   rafraîchir. Recliquer sur un état périmé échouerait à l'identique, et le
 *   refus paraîtrait entêté.
 *
 * Ne fait pas : elle ne recharge rien à l'écran, n'affiche aucun message et ne
 *   rejoue JAMAIS deux fois — un conflit qui persiste après une relecture est
 *   un vrai conflit, et il se dit.
 */

import { type Issue } from './api.ts';
import { listeLesProjets } from './api-projet.ts';

/** La version que le serveur porte à l'instant, ou `null` s'il ne la dit pas. */
async function versionFraiche(projetId: string): Promise<number | null> {
  const frais = await listeLesProjets();
  if (!frais.bon) return null;

  return frais.valeur.find((ligne) => ligne.id === projetId)?.version ?? null;
}

/**
 * Envoie une écriture arbitrée par version, et la rejoue une fois si — et
 * seulement si — le serveur a répondu « conflit ».
 *
 * `envoie` reçoit la version à porter : c'est le seul paramètre qui change
 * entre les deux tentatives.
 */
export async function ecritLeProjet(
  projetId: string,
  version: number,
  envoie: (version: number) => Promise<Issue<void>>,
): Promise<Issue<void>> {
  const premiere = await envoie(version);
  if (premiere.bon || premiere.erreur.famille !== 'conflit') return premiere;

  const aJour = await versionFraiche(projetId);

  // Sans version fraîche, on rend le PREMIER refus : c'est celui qui dit ce
  // qui s'est passé. Inventer une seconde tentative à l'aveugle écraserait
  // peut-être le travail de quelqu'un d'autre.
  if (aJour === null) return premiere;

  return envoie(aJour);
}
